import { useCallback, useEffect, useMemo, useState } from "react"
import { useOutletContext } from "react-router-dom"

import EmailPreference from "./email-preference"
import {
  useFetchLoanOfficerQuery,
  useUpdateLoanOfficerMutation
} from "../../../../redux/queries"
import { useAppDispatch, useToast } from "../../../hooks"
import { toggleProfileUpdated } from "../../../../redux/slices/auth"
import { mapSettingsToPreferences } from "../../../utils"
import { EMAIL_PREFERENCES } from "../../../constants"
import { TEmailPreferenceKey, UserDetails } from "../../../../types"

interface OutletContext {
  user: UserDetails
}

const MyEmailPreferences = () => {
  const { user } = useOutletContext<OutletContext>()

  const dispatch = useAppDispatch()
  const { pushErrorToast, pushSuccessToast } = useToast()

  const [preferences, setPreferences] = useState<
    Record<TEmailPreferenceKey, boolean>
  >(() => mapSettingsToPreferences(EMAIL_PREFERENCES, true))
  const { data: loDetails, isFetching } = useFetchLoanOfficerQuery(user.id, {
    skip: !user.id
  })

  const [updateLoanOfficer, { isSuccess, isError }] =
    useUpdateLoanOfficerMutation()

  const handleUpdatePreference = useCallback(
    (key: TEmailPreferenceKey) => {
      if (!loDetails) return

      setPreferences(prev => ({ ...prev, [key]: !prev[key] }))
      updateLoanOfficer({
        ...loDetails,
        notification_settings: {
          ...loDetails.notification_settings,
          [key]: !preferences[key]
        }
      })
    },
    [loDetails, updateLoanOfficer, preferences]
  )

  const renderEmailPreferences = useMemo(
    () =>
      EMAIL_PREFERENCES.map((preference, index) => (
        <EmailPreference
          key={`${preference.key}-${index}`}
          preference={preference}
          isEnabled={preferences[preference.key]}
          onEnablePreference={handleUpdatePreference}
        />
      )),
    [handleUpdatePreference, preferences]
  )

  useEffect(() => {
    if (isSuccess) {
      pushSuccessToast("Email preferences updated.")
      dispatch(toggleProfileUpdated(true))
    }
    if (isError) {
      pushErrorToast("Failed to update account.")
    }
  }, [isSuccess, isError, pushErrorToast, pushSuccessToast, dispatch])

  useEffect(() => {
    if (isFetching) return

    if (!isFetching && loDetails) {
      const { notification_settings } = loDetails

      setPreferences(
        mapSettingsToPreferences(
          EMAIL_PREFERENCES,
          false,
          notification_settings
        )
      )
    }
  }, [isFetching, loDetails])

  return (
    <div className="px-6">
      <h2 className="font-bold text-xl pt-6">Email Preferences</h2>

      <div className="pt-2 pb-24">{renderEmailPreferences}</div>
    </div>
  )
}

export default MyEmailPreferences
